import React, { useState, useContext, useRef } from 'react';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';
import { Link, MaskedLink, VerticalListing, StructuredListing } from '@hz-design-system/web-ui';
import { formatImageUrl, ImageSize } from '@ecg-marktplaats/media-util-js-lib';
import EnvironmentContext from '../../../../contexts/EnvironmentContext';
import { trackGAEvent } from '../../../../utils/gaTracking';
import { getSessionStorageItem, setSessionStorageItem } from '../../../../utils/sStorage';
import formatPrice from './utils/formatPrice';
import { TFeedItem } from 'types/TFeeds';
import deleteFavorite from '../../repositories/deleteFavorite';
import createFavorite from '../../repositories/createFavorite';
import showToastNotification, { dismissToast } from '../Toast/ToastNotification';

type TFeedItemProps = {
  item: TFeedItem;
  lazyLoadImage: boolean;
  onClick: Function;
  feedType: string;
  feedPostCode?: string;
  setShowLoginDialog: Function;
};

const updateFeeds = (item: TFeedItem, feedType: string, isFavorited: boolean, feedPostCode?: string) => {
  const storageKey = feedPostCode ? `HOMEPAGE_FEED_${feedType}_${feedPostCode}` : `HOMEPAGE_FEED_${feedType}`;
  const cachedData = getSessionStorageItem(storageKey);
  if (cachedData?.feedItems) {
    const updatedFeeds = cachedData.feedItems.map((feed) => {
      return feed.itemId === item.itemId ? { ...feed, isFavorited } : feed;
    });
    setSessionStorageItem(storageKey, {
      feedItemsPage: cachedData?.feedItemsPage,
      feedItems: updatedFeeds,
    });
  }
};

const FeedItem = ({ item, lazyLoadImage, onClick, feedType, feedPostCode, setShowLoginDialog }: TFeedItemProps) => {
  const { t } = useI18nContext();
  const { experiments, xsrfToken, userLoggedIn } = useContext(EnvironmentContext);

  const { isNewHomepageListingEnabled } = experiments;

  const [url, setUrl] = useState(item.url);
  const [isFavorite, setIsFavorite] = useState(item.isFavorited ? item.isFavorited : false);

  const closeToast = useRef<any>(null);

  // eslint-disable-next-line no-undefined
  const imageLoading = lazyLoadImage ? 'lazy' : undefined;
  const imageDecoding = lazyLoadImage ? 'async' : undefined;

  const validateResoponse = (response, isFav: Boolean) => {
    if (closeToast.current) {
      closeToast.current();
    }
    closeToast.current = dismissToast;

    if (response && 'isFavorite' in response && isFav !== response.isFavorite) {
      showToastNotification({
        type: 'success',
        description: t(
          response.isFavorite
            ? 'homepage.listing.favourite.notifications.favoriting'
            : 'homepage.listing.favourite.notifications.unfavoriting',
        ),
      });

      if (response.isFavorite) {
        trackGAEvent('WatchlistAddSuccess');
      } else {
        trackGAEvent('WatchlistRemoveSuccess');
      }
      return true;
    } else {
      showToastNotification({
        type: 'error',
        description: t('page.error.error_global_ajax'),
      });
      return false;
    }
  };

  const onFavorite = async () => {
    const isFav = isFavorite;
    if (userLoggedIn) {
      setIsFavorite(!isFav);
      let response;
      if (isFav) {
        trackGAEvent('WatchlistRemoveBegin');
        response = await deleteFavorite(item.itemId, xsrfToken);
        const hasValidData = validateResoponse(response, isFav);
        if (hasValidData) {
          updateFeeds(item, feedType, false, feedPostCode);
        }
        trackGAEvent('WatchlistRemoveSuccess');
      } else {
        trackGAEvent('WatchlistAddBegin');
        response = await createFavorite(item.itemId, xsrfToken);
        const hasValidData = validateResoponse(response, isFav);
        if (hasValidData) {
          updateFeeds(item, feedType, true, feedPostCode);
        }
      }

      // actual update
      setIsFavorite(response.isFavorite);
    } else {
      setShowLoginDialog(true);
      // create favorite item in session storage
      setSessionStorageItem('FAVORITE_ITEM', item);
    }
  };

  if (isNewHomepageListingEnabled) {
    return (
      <StructuredListing
        title={item.title}
        price={formatPrice(item.price, t)}
        imageSrc={formatImageUrl(item.picture?.url, ImageSize.Large, item.picture?.sizes)}
        imageLoading={imageLoading}
        decoding={imageDecoding}
        isMaskedLink={item.thinContent ? true : false}
        onHeart={onFavorite}
        url={url}
        onClick={() => onClick(item.correlationId)}
        onContextMenu={() => {
          const newUrl = `${item.url}${item.url.includes('?') ? '&' : '?'}correlationId=${item.correlationId}`;
          setUrl(newUrl);
        }}
        isFavorite={isFavorite}
        showFav={true}
      />
    );
  } else {
    const LinkComponent = item.thinContent ? MaskedLink : Link;
    return (
      <LinkComponent
        kind="block"
        href={url}
        onClick={() => onClick(item.correlationId)}
        onContextMenu={() => {
          const newUrl = `${item.url}${item.url.includes('?') ? '&' : '?'}correlationId=${item.correlationId}`;
          setUrl(newUrl);
        }}
      >
        <VerticalListing
          title={item.title}
          price={formatPrice(item.price, t)}
          imageSrc={formatImageUrl(item.picture?.url, ImageSize.Large, item.picture?.sizes)}
          imageLoading={imageLoading}
          decoding={imageDecoding}
          imageOverlayLabel={item.isHomepageAdLabel ? t('homepage.feeds.item.traits.homepage_ad') : ''}
        />
      </LinkComponent>
    );
  }
};

export default React.memo(FeedItem);
